import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { DEBOUNCE_DELAY } from "../utils";

type QueryParams = Record<string, unknown>;

const debounce = <Args extends unknown[]>(
  func: (...args: Args) => void,
  delay: number
): ((...args: Args) => void) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: Args) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const useDebouncedFunction = <Args extends unknown[]>(
  callback: (...args: Args) => void,
  delay: number
): ((...args: Args) => void) => {
  const callbackRef = useRef(callback);

  useLayoutEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useMemo(
    () => debounce((...args: Args) => callbackRef.current(...args), delay),
    [delay]
  );
};

/** Drop params whose values match the schema defaults so URLs stay clean. */
export const stripDefaults = <T extends QueryParams>(
  params: T,
  defaults: T | null
): T => {
  if (!defaults) return params;

  return Object.fromEntries(
    Object.entries(params).filter(
      ([key, value]) => JSON.stringify(value) !== JSON.stringify(defaults[key])
    )
  ) as T;
};

export default function useDebouncedQueryParams<T extends QueryParams = QueryParams>(
  debouncedParams: Array<Extract<keyof T, string>> = [],
  schemaDefaults: T | null = null
): {
  debouncedQueryParams: T;
  setDebouncedQueryParams: (changes: Partial<T>) => void;
  rawValues: T;
  setRawValues: (changes: Partial<T>) => void;
  queryParams: T;
} {
  const navigate = useNavigate();
  // TanStack Router's useSearch expects a registered route type; cast needed for generic usage
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const searchParams = useSearch({ strict: false } as any) as T;

  const [rawValues, setRawValuesState] = useState<T>(searchParams);
  const lastUpdate = useRef<T>(searchParams);

  const debouncedNavigate = useDebouncedFunction(
    (newParams: Partial<T>) => {
      // TanStack Router's navigate `search` updater expects route-specific types; cast at boundary
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigate({
        search: ((prev: T) =>
          schemaDefaults
            ? stripDefaults({ ...prev, ...newParams } as T, schemaDefaults)
            : ({ ...prev, ...newParams } as T)) as any,
      });
      lastUpdate.current = { ...lastUpdate.current, ...newParams };
    },
    DEBOUNCE_DELAY
  );

  useEffect(() => {
    if (JSON.stringify(searchParams) !== JSON.stringify(lastUpdate.current)) {
      setRawValuesState(searchParams);
    }
  }, [searchParams]);

  const pick = (
    obj: T,
    ...keys: Array<Extract<keyof T, string>>
  ): Partial<T> =>
    Object.fromEntries(
      keys.filter((key) => key in obj).map((key) => [key, obj[key]])
    ) as unknown as Partial<T>;

  const queryParams: T = {
    ...rawValues,
    ...pick(searchParams, ...debouncedParams),
  } as T;

  return {
    debouncedQueryParams: searchParams,
    setDebouncedQueryParams: debouncedNavigate,
    rawValues,
    setRawValues: (changes: Partial<T>) => {
      setRawValuesState((prev) => ({ ...prev, ...changes }));
      debouncedNavigate(changes);
    },
    queryParams,
  };
}

